// src/config/apiError.ts
//
// The one place an HTTP failure becomes a user-facing Error. fetchWithAuth (config/api.ts) and the
// auth calls in AuthContext (which can't use fetchWithAuth: its 401 handling redirects to /login,
// and a wrong password is not a session expiry) both go through here, so every screen gets the
// same shape: `message` is something a person can read — never raw JSON or an HTML error page —
// and `status` / `response.data` stay available to callers that branch on the server's body.

export const NETWORK_ERROR_MESSAGE = "Can't reach DA Connect. Please check your connection and try again.";
export const TIMEOUT_ERROR_MESSAGE = 'The request timed out. Please try again.';
export const RATE_LIMIT_MESSAGE = 'Too many attempts. Please wait a minute and try again.';
export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.';
export const SERVER_ERROR_MESSAGE = 'Something went wrong on our side. Please try again.';

/** Default timeout for requests that don't choose their own. */
export const DEFAULT_REQUEST_TIMEOUT = 30000;

export interface ApiErrorResponse {
    status: number;
    statusText: string;
    data?: unknown;
    headers?: Record<string, string>;
    text?: string;
}

export type ApiErrorKind = 'http' | 'network' | 'timeout';

export class ApiError extends Error {
    readonly kind: ApiErrorKind;
    /** HTTP status; undefined for network failures and timeouts. */
    readonly status?: number;
    readonly response?: ApiErrorResponse;
    /** The body was an HTML page (a proxy/gateway error, not our API). */
    readonly isHtmlError?: boolean;

    constructor(message: string, opts: { kind: ApiErrorKind; response?: ApiErrorResponse; isHtmlError?: boolean }) {
        super(message);
        this.name = 'ApiError';
        this.kind = opts.kind;
        this.response = opts.response;
        this.status = opts.response?.status;
        this.isHtmlError = opts.isHtmlError;
    }
}

/** Copy for a failed status when the server gave us nothing a person should read. */
export function statusFallbackMessage(status: number): string {
    if (status === 401) return SESSION_EXPIRED_MESSAGE;
    if (status === 403) return "You don't have permission to do that.";
    if (status === 404) return 'Not found.';
    if (status === 413) return 'That file is too large.';
    if (status === 429) return RATE_LIMIT_MESSAGE;
    if (status >= 500) return SERVER_ERROR_MESSAGE;
    return "That didn't work — please check and try again.";
}

const MAX_PLAIN_TEXT_MESSAGE = 200;

const nonEmptyString = (value: unknown): string | null =>
    typeof value === 'string' && value.trim() ? value.trim() : null;

const looksLikeMarkup = (text: string) => /^\s*</.test(text);

/**
 * The user-facing message inside an error body, if there is one:
 * `{ message }` | `{ error }` | ProblemDetails (first field error, else `title`) | a JSON string
 * (`BadRequest("…")` serialises as `"…"`) | short plain text.
 *
 * On a 5xx only a deliberate JSON-object `{ message }` / `{ error }` counts: a ProblemDetails title or
 * field error, or a bare string body, there is framework/exception text or a proxy's "Bad Gateway",
 * never copy meant for the user — they get the friendly 5xx copy.
 */
export function extractServerMessage(data: unknown, text: string, status: number): string | null {
    if (data && typeof data === 'object') {
        const body = data as { message?: unknown; error?: unknown; title?: unknown; errors?: unknown };
        const direct = nonEmptyString(body.message) ?? nonEmptyString(body.error);
        if (direct) return direct;
        // A 5xx ProblemDetails title / field error is framework or exception text, not copy for
        // the user ("An error occurred while processing your request.").
        if (status >= 500) return null;

        if (body.errors && typeof body.errors === 'object') {
            for (const value of Object.values(body.errors as Record<string, unknown>)) {
                const first = Array.isArray(value) ? nonEmptyString(value[0]) : nonEmptyString(value);
                if (first) return first;
            }
        }
        return nonEmptyString(body.title);
    }

    if (status >= 500) return null;

    if (typeof data === 'string') return nonEmptyString(data);

    const plain = text.trim();
    if (plain && plain.length <= MAX_PLAIN_TEXT_MESSAGE && !looksLikeMarkup(plain)) return plain;
    return null;
}

/**
 * The server's own user-facing message on a 4xx ApiError — copy it wrote for people ("Coupon has
 * expired") — or null for a 5xx, a network failure / timeout, a 4xx with nothing readable in its
 * body, or anything that isn't an ApiError. For screens that prefer their own friendly copy unless
 * the server said something specific about the request.
 */
export function clientErrorServerMessage(err: unknown): string | null {
    if (!(err instanceof ApiError) || err.kind !== 'http') return null;
    const status = err.status ?? 0;
    if (status < 400 || status >= 500) return null;
    return extractServerMessage(err.response?.data, err.response?.text ?? '', status);
}

/** Reads a response body once: parsed JSON when it is JSON, plus the raw text either way. */
export async function readBody(response: Response): Promise<{ data: unknown; text: string; parsed: boolean }> {
    const text = await response.text().catch(() => '');
    if (!text.trim()) return { data: undefined, text, parsed: false };
    try {
        return { data: JSON.parse(text), text, parsed: true };
    } catch {
        return { data: undefined, text, parsed: false };
    }
}

/** Whether a body is an HTML page — a proxy error page, or the SPA's index.html. */
export const isMarkup = (text: string) => looksLikeMarkup(text);

/**
 * Builds the error for a response whose body has already been read: any non-2xx, or a 2xx that
 * came back as an HTML page (the SPA fallback answering a misrouted /api call), which is not the
 * data the caller asked for.
 */
export function buildHttpError(response: Response, body: { data: unknown; text: string }): ApiError {
    const isHtmlError = looksLikeMarkup(body.text);
    const message = response.ok
        ? SERVER_ERROR_MESSAGE
        : extractServerMessage(body.data, body.text, response.status) ?? statusFallbackMessage(response.status);
    return new ApiError(message, {
        kind: 'http',
        isHtmlError,
        response: {
            status: response.status,
            statusText: response.statusText,
            data: body.data,
            headers: Object.fromEntries(response.headers.entries()),
            text: body.text.length > 500 ? `${body.text.substring(0, 500)}...` : body.text,
        },
    });
}

/**
 * Maps what `fetch` itself throws to a friendly error: an abort (our timeout) and a TypeError
 * (Chrome "Failed to fetch", Safari "Load failed", Firefox "NetworkError…") are not the user's
 * input being wrong. Anything already an ApiError passes through unchanged.
 */
export function toRequestError(err: unknown): unknown {
    if (err instanceof ApiError) return err;
    if (err && typeof err === 'object' && (err as { name?: string }).name === 'AbortError') {
        return new ApiError(TIMEOUT_ERROR_MESSAGE, { kind: 'timeout' });
    }
    // ANY TypeError reaching here is treated as a network failure: callers only wrap the fetch
    // and body read in the try, and that is what fetch throws when the request never completes.
    // (A TypeError from a bug in that small block would be misreported as "can't reach".)
    if (err instanceof TypeError) {
        return new ApiError(NETWORK_ERROR_MESSAGE, { kind: 'network' });
    }
    return err;
}

/**
 * The text an error display should classify an error by. An ApiError is classified by its kind and
 * status — its message is the server's copy and may contain any number ("must be under 500
 * characters"), so sniffing digits in it misfiles errors. Anything else falls back to its message.
 */
export function errorClassText(error: unknown): string {
    if (error instanceof ApiError) {
        if (error.kind === 'network') return 'network connection';
        if (error.kind === 'timeout') return 'timed out';
        const status = error.status ?? 0;
        if (status === 401) return 'unauthorized';
        if (status === 403) return 'forbidden';
        if (status === 404) return 'not found';
        if (status === 409) return 'conflict';
        if (status === 429) return 'too many';
        if (status >= 500) return 'server error';
        return '';
    }
    const message = error instanceof Error ? error.message : typeof error === 'string' ? error : String(error ?? '');
    return message.toLowerCase();
}

/** The message to show for anything thrown by a request, with a fallback for the unexpected. */
export function errorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
    const mapped = toRequestError(err);
    return mapped instanceof Error && mapped.message ? mapped.message : fallback;
}

/**
 * A plain JSON request with a timeout and the shared error shape, for callers that must NOT get
 * fetchWithAuth's session-expiry redirect (sign-in, registration). Resolves with the parsed body
 * on 2xx; throws an ApiError otherwise.
 */
export async function requestJson<T = unknown>(
    url: string,
    init: RequestInit & { timeout?: number } = {},
): Promise<T> {
    const { timeout = DEFAULT_REQUEST_TIMEOUT, ...fetchInit } = init;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    try {
        const response = await fetch(url, { ...fetchInit, signal: controller.signal });
        const body = await readBody(response);
        // A 2xx HTML page (SPA fallback for a misrouted call) is not the JSON asked for.
        if (!response.ok || isMarkup(body.text)) throw buildHttpError(response, body);
        return body.data as T;
    } catch (err) {
        throw toRequestError(err);
    } finally {
        clearTimeout(timeoutId);
    }
}
